import React, { useState, useRef, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../contexts/AuthContext';

const DocumentsV4 = () => {
  const { getAuthHeaders } = useAuth();
  const [file, setFile] = useState(null);
  const [job, setJob] = useState(null);
  const [progress, setProgress] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFilters, setSearchFilters] = useState({ file_ids: '', file_names: '' });
  const [searchResults, setSearchResults] = useState([]);
  const [searchTookMs, setSearchTookMs] = useState(null);
  const [activeTab, setActiveTab] = useState('upload');
  // TODO: This monolith page will be split into routes; keep legacy tab state for now
  const [embConfig, setEmbConfig] = useState({ api_key: '', model: 'text-embedding-3-small' });
  const [docs, setDocs] = useState({ items: [], total: 0 });
  const [pagination, setPagination] = useState({ limit: 25, offset: 0, q: '' });
  const [verifyResult, setVerifyResult] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);
  const poller = useRef(null);

  const loadDocs = useCallback(async (limit = pagination.limit, offset = pagination.offset, q = pagination.q) => {
    try {
      const params = new URLSearchParams({ limit: String(limit), offset: String(offset), q: q || '' });
      const res = await fetch(`${API_BASE_URL}/api/v4/documents/list?${params.toString()}`, {
        headers: { ...getAuthHeaders() },
      });
      if (!res.ok) {
        setDocs({ items: [], total: 0 });
        return;
      }
      const data = await res.json();
      setDocs({
        items: Array.isArray(data.items) ? data.items : [],
        total: typeof data.total === 'number' ? data.total : 0,
        limit: data.limit,
        offset: data.offset,
        q: data.q || q || '',
      });
    } catch (e) {
      setDocs({ items: [], total: 0, q });
    }
  }, [API_BASE_URL, getAuthHeaders, pagination.limit, pagination.offset, pagination.q]);
  const editMarkdown = async (doc) => {
    const res = await fetch(`${API_BASE_URL}/api/v4/documents/${doc.id}/markdown`, {
      headers: { ...getAuthHeaders() },
    });
    const data = await res.json();
    const current = data.markdown || '';
    const edited = window.prompt('Edit markdown:', current);
    if (edited == null) return;
    const saveRes = await fetch(`${API_BASE_URL}/api/v4/documents/${doc.id}/markdown`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ markdown: edited }),
    });
    if (!saveRes.ok) {
      alert('Failed to save markdown');
      return;
    }
    await loadDocs();
  };

  const viewMarkdown = (doc) => {
    const url = doc.markdown_url ? `${API_BASE_URL}${doc.markdown_url}` : null;
    if (!url) return;
    window.open(url, '_blank');
  };

  const deleteDocument = async (doc) => {
    if (!window.confirm(`Delete ${doc.original_filename}?`)) return;
    // Prefer id; if id looks non-UUID, fall back to by-job
    const isUuid = typeof doc.id === 'string' && /[0-9a-fA-F-]{36}/.test(doc.id);
    const url = isUuid
      ? `${API_BASE_URL}/api/v4/documents/${doc.id}`
      : `${API_BASE_URL}/api/v4/documents/by-job/${doc.job_id}`;
    const res = await fetch(url, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) {
      alert('Delete failed');
      return;
    }
    await loadDocs();
  };

  useEffect(() => { loadDocs(); }, [loadDocs]);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/v4/documents/config/embeddings`, { headers: { ...getAuthHeaders() } });
        if (res.ok) {
          const data = await res.json();
          setEmbConfig({
            api_key: data.config?.api_key || '',
            model: data.config?.model || 'text-embedding-3-small',
          });
        }
      } catch {}
    })();
  }, [API_BASE_URL, getAuthHeaders]);

  const submitUpload = async () => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    form.append('is_zip', String(file.name.toLowerCase().endsWith('.zip')));

    // Avoid forcing Content-Type for multipart; copy only auth header
    const authHeaders = getAuthHeaders();
    if ('Content-Type' in authHeaders) delete authHeaders['Content-Type'];

    setUploading(true);
    const res = await fetch(`${API_BASE_URL}/api/v4/documents/upload`, {
      method: 'POST',
      headers: { ...authHeaders },
      body: form,
    });
    if (!res.ok) {
      try {
        const err = await res.json();
        alert(`Upload failed: ${res.status} ${err.detail || ''}`);
      } catch (_) {
        alert(`Upload failed: ${res.status}`);
      }
      setUploading(false);
      return;
    }
    const data = await res.json();
    setJob(data);
    if (data.job_id) startPolling(data.job_id);
    await loadDocs();
  };

  const startPolling = (jobId) => {
    if (poller.current) clearInterval(poller.current);
    poller.current = setInterval(async () => {
      const res = await fetch(`${API_BASE_URL}/api/v4/documents/jobs/${jobId}`, {
        headers: { ...getAuthHeaders() },
      });
      const data = await res.json();
      setProgress(data);
      if (data.status === 'completed' || data.status === 'failed') {
        clearInterval(poller.current);
        await loadDocs();
        setUploading(false);
        // Clear job details shortly after finishing
        setTimeout(() => {
          setJob(null);
          setProgress(null);
        }, 1200);
      } else {
        // also refresh docs to reflect progress in list
        await loadDocs();
      }
    }, 1500);
  };

  const clearQueue = async () => {
    if (!window.confirm('Clear all queued/processing jobs?')) return;
    const res = await fetch(`${API_BASE_URL}/api/v4/documents/queue/clear`, {
      method: 'DELETE',
      headers: { ...getAuthHeaders() },
    });
    if (!res.ok) {
      alert('Failed to clear queue');
      return;
    }
    await loadDocs();
  };

  const runSearch = async () => {
    const params = new URLSearchParams({
      query: searchQuery,
      file_ids: searchFilters.file_ids,
      file_names: searchFilters.file_names,
      top_k: '8',
    });
    const res = await fetch(`${API_BASE_URL}/api/v4/documents/search?${params.toString()}`, {
      headers: { ...getAuthHeaders() },
    });
    const data = await res.json();
    const results = Array.isArray(data.results)
      ? data.results
      : (Array.isArray(data?.results?.matches) ? data.results.matches : []);
    setSearchResults(results);
    setSearchTookMs(typeof data.took_ms === 'number' ? data.took_ms : null);
  };

  const verifyPinecone = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/v4/documents/search?query=__ping__&top_k=1`, {
        headers: { ...getAuthHeaders() },
      });
      const data = await res.json();
      setVerifyResult(data);
    } catch (e) {
      setVerifyResult({ error: String(e) });
    }
  };

  const loadDiagnostics = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/v4/documents/pinecone/diagnostics`, {
        headers: { ...getAuthHeaders() },
      });
      const data = await res.json();
      setDiagnostics(data);
    } catch (e) {
      setDiagnostics({ error: String(e) });
    }
  };

  return (
    <div className="container py-4">
      <h3 className="mb-3">Documents v4</h3>
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'upload' ? 'active' : ''}`} onClick={() => setActiveTab('upload')}>Upload</button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'search' ? 'active' : ''}`} onClick={() => setActiveTab('search')}>Search</button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>Settings</button>
        </li>
      </ul>

      {activeTab === 'upload' && (
      <div className="card mb-4">
        <div className="card-body">
          <div className="mb-2">Upload a document or a .zip for batch processing:</div>
          <label className="d-block border border-2 border-dashed rounded p-4 text-center bg-light" style={{ cursor: 'pointer' }}>
            <input type="file" className="d-none" onChange={(e) => setFile(e.target.files[0])} />
            <div className="mb-2">
              <span className="badge bg-primary-subtle text-primary">Choose file</span>
            </div>
            <div className="text-muted small">{file ? file.name : 'Click to select a file (PDF, DOCX, XLSX, ZIP, etc.)'}</div>
          </label>
          <div className="mt-3 d-flex gap-2">
            <button className="btn btn-primary" onClick={submitUpload} disabled={!file || uploading}>Start Processing</button>
            <button className="btn btn-outline-secondary" onClick={() => setFile(null)}>Clear</button>
          </div>
          {job && (
            <div className="mt-3 small text-muted">Job: {job.job_id} (batch {job.batch_id}) - {job.status}</div>
          )}
          {progress && (
            <>
              <div className="mt-2 small">Status: {progress.status} | Progress: {progress.progress}%</div>
              <div className="progress mt-1" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow={Number(progress.progress || 0)}>
                <div className="progress-bar" style={{ width: `${Number(progress.progress || 0)}%` }}>
                  {Number(progress.progress || 0)}%
                </div>
              </div>
              {progress.metadata?.pipeline && Array.isArray(progress.metadata.pipeline) && (
                <div className="mt-3">
                  <div className="fw-semibold small mb-2">Pipeline</div>
                  <ul className="list-group">
                    {progress.metadata.pipeline.map((step, i) => (
                      <li key={i} className="list-group-item d-flex justify-content-between align-items-start">
                        <div>
                          <div className="fw-semibold">{step.name}</div>
                          <div className="small text-muted">{step.message}</div>
                        </div>
                        <span className={`badge ${step.status === 'completed' ? 'bg-success' : step.status === 'failed' ? 'bg-danger' : 'bg-warning text-dark'}`}>{step.status}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      )}

      {activeTab === 'search' && (
      <div className="card">
        <div className="card-body">
          <h6 className="mb-3">Pinecone Search</h6>
          <div className="row g-2 mb-3">
            <div className="col-md-6">
              <input className="form-control" placeholder="Query" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            </div>
            <div className="col-md-3">
              <input className="form-control" placeholder="File IDs (csv)" value={searchFilters.file_ids} onChange={(e) => setSearchFilters(s => ({ ...s, file_ids: e.target.value }))} />
            </div>
            <div className="col-md-3">
              <input className="form-control" placeholder="File names (csv)" value={searchFilters.file_names} onChange={(e) => setSearchFilters(s => ({ ...s, file_names: e.target.value }))} />
            </div>
          </div>
          <button className="btn btn-outline-primary" onClick={runSearch}>Search</button>
          <button className="btn btn-outline-secondary ms-2" onClick={verifyPinecone}>Verify Pinecone</button>
          <button className="btn btn-outline-info ms-2" onClick={loadDiagnostics}>Diagnostics</button>

          <div className="mt-3">
            {typeof searchTookMs === 'number' && (
              <div className="text-muted small mb-2">Results in {searchTookMs} ms</div>
            )}
            {(Array.isArray(searchResults) ? searchResults : []).map((r, idx) => {
              const meta = r?.metadata || {};
              const title = meta.original_filename || meta.path || r.id;
              const snippet = r?.chunk || '';
              return (
                <div key={idx} className="border rounded p-2 mb-2">
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="fw-semibold">{title}</div>
                    <div className="text-muted small">score: {typeof r.score === 'number' ? r.score.toFixed(3) : '-'}</div>
                  </div>
                  {snippet ? (
                    <div className="mt-1 small" style={{ whiteSpace: 'pre-wrap' }}>{snippet}</div>
                  ) : (
                    <div className="mt-1 small text-muted">No snippet available</div>
                  )}
                  <details className="mt-2">
                    <summary className="small text-muted">Raw</summary>
                    <pre className="mb-0 small">{JSON.stringify(r, null, 2)}</pre>
                  </details>
                </div>
              );
            })}
            {verifyResult && (
              <div className="accordion mt-3" id="verifyAccordion">
                <div className="accordion-item">
                  <h2 className="accordion-header" id="headingVerify">
                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseVerify" aria-expanded="false" aria-controls="collapseVerify">
                      Verification JSON
                    </button>
                  </h2>
                  <div id="collapseVerify" className="accordion-collapse collapse" aria-labelledby="headingVerify" data-bs-parent="#verifyAccordion">
                    <div className="accordion-body">
                      <pre className="mb-0 small">{JSON.stringify(verifyResult, null, 2)}</pre>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {diagnostics && (
              <div className="accordion mt-3" id="diagAccordion">
                <div className="accordion-item">
                  <h2 className="accordion-header" id="headingDiag">
                    <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#collapseDiag" aria-expanded="true" aria-controls="collapseDiag">
                      Pinecone Diagnostics
                    </button>
                  </h2>
                  <div id="collapseDiag" className="accordion-collapse collapse show" aria-labelledby="headingDiag" data-bs-parent="#diagAccordion">
                    <div className="accordion-body">
                      <pre className="mb-0 small">{JSON.stringify(diagnostics, null, 2)}</pre>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {activeTab === 'settings' && (
        <div className="card">
          <div className="card-body">
            <h6 className="mb-3">Embeddings (v4)</h6>
            <div className="row g-3">
              <div className="col-md-8">
                <label className="form-label">OpenAI API Key</label>
                <input type="password" className="form-control" value={embConfig.api_key} onChange={(e) => setEmbConfig({ ...embConfig, api_key: e.target.value })} placeholder="Enter key" />
              </div>
              <div className="col-md-4">
                <label className="form-label">Model</label>
                <input className="form-control" value={embConfig.model} onChange={(e) => setEmbConfig({ ...embConfig, model: e.target.value })} placeholder="text-embedding-3-small" />
              </div>
            </div>
            <div className="mt-3">
              <button className="btn btn-primary" onClick={async () => {
                const res = await fetch(`${API_BASE_URL}/api/v4/documents/config/embeddings`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                  body: JSON.stringify(embConfig),
                });
                if (res.ok) alert('Embeddings config saved'); else alert('Save failed');
              }}>Save</button>
            </div>
          </div>
        </div>
      )}

      <div className="card mt-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Recent v4 Documents</h6>
            <div className="d-flex align-items-center gap-2">
              <button className="btn btn-outline-danger btn-sm" onClick={clearQueue}>Clear Queue</button>
              <div className="text-muted small">Total: {docs.total}</div>
            </div>
          </div>
          <div className="table-responsive">
              <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Pinecone Vector ID</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(docs.items || []).map((d) => (
                  <tr key={d.id}>
                    <td>{d.original_filename}</td>
                      <td style={{ wordBreak: 'break-all' }}>{d.pinecone_vector_id || '-'}</td>
                    <td><span className="badge bg-secondary">{d.status}</span></td>
                    <td>{d.progress}%</td>
                    <td>{new Date(d.created_at).toLocaleString()}</td>
                    <td className="text-nowrap" style={{ minWidth: 180 }}>
                      <div className="d-flex flex-wrap gap-2">
                        <button
                          className="btn btn-outline-primary d-flex align-items-center justify-content-center"
                          style={{ minWidth: 44, minHeight: 44 }}
                          title="View"
                          disabled={!d.markdown_url}
                          onClick={() => viewMarkdown(d)}
                        >
                          <i className="fa-solid fa-eye fa-lg"></i>
                        </button>
                        <button
                          className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
                          style={{ minWidth: 44, minHeight: 44 }}
                          title="Edit"
                          disabled={!d.markdown_url}
                          onClick={() => editMarkdown(d)}
                        >
                          <i className="fa-solid fa-pen-to-square fa-lg"></i>
                        </button>
                        <button
                          className="btn btn-outline-danger d-flex align-items-center justify-content-center"
                          style={{ minWidth: 44, minHeight: 44 }}
                          title="Delete"
                          onClick={() => deleteDocument(d)}
                        >
                          <i className="fa-solid fa-trash fa-lg"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                  {!docs.items || docs.items.length === 0 ? (
                    <tr><td colSpan="6" className="text-center text-muted py-3">No documents yet</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
          <div className="d-flex justify-content-end gap-2 mt-3">
            <button className="btn btn-outline-secondary btn-sm" disabled={pagination.offset === 0}
              onClick={() => { const next = Math.max(0, pagination.offset - pagination.limit); setPagination({ ...pagination, offset: next }); loadDocs(pagination.limit, next); }}>
              Prev
            </button>
            <button className="btn btn-outline-secondary btn-sm" disabled={(pagination.offset + (docs.items?.length || 0)) >= docs.total}
              onClick={() => { const next = pagination.offset + pagination.limit; setPagination({ ...pagination, offset: next }); loadDocs(pagination.limit, next); }}>
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentsV4;


